import { launchBlocker } from './selection';

import type { BulkModule } from '../schemas/bulk-migration';

/**
 * The launch dialog's words and its submit rule, as plain data.
 *
 * Three things must all be true to submit, and each is the operator's own
 * statement rather than a default: how many (the count, within the cap), why
 * (a reason, which the runner stamps on every run it starts), and that they
 * mean it (the typed phrase the release turn-off already uses).
 *
 * At most one more is optional and OFF until ticked, chosen by the module's
 * `rerun` (INI-0035 D4):
 *
 * - `update` — **Update from Salesforce** (`resync`): bring Salesforce's changes
 *   to records already migrated;
 * - `re-evaluate` — **Re-evaluate all records** (`rearm: 'all'`): a launch already
 *   re-evaluates the records whose Salesforce record changed; this re-evaluates
 *   every one, the operator's catch-all;
 * - `none` — neither: a seed-once module never touches what it migrated.
 *
 * An API from before `rerun` does not send it: the dialog then offers the
 * re-sync on `supportsResync` alone, as it always did, and never the full
 * re-evaluation (that runner has none).
 */

export const MAX_REASON = 500;

function clients(count: number): string {
    return `${count.toLocaleString('en-US')} ${count === 1 ? 'client' : 'clients'}`;
}

export const RESYNC_OPTION_LABEL = 'Update from Salesforce';

export const RESYNC_OPTION_HELP =
    'Records already migrated are read from Salesforce again and its changes are brought ' +
    'over; what the module protects in Veevart is left as it is.';

export const REARM_OPTION_LABEL = 'Re-evaluate all records (ignores change detection)';

export const REARM_OPTION_HELP =
    'Every launch already re-evaluates the records whose Salesforce record changed. Tick ' +
    'this to re-evaluate every record already migrated, changed or not, for example when ' +
    'links between records look out of date. What was changed in Veevart is kept.';

type RerunFields = Pick<BulkModule, 'rerun' | 'supportsResync'>;

/** Whether the dialog offers "Update from Salesforce" (the re-sync) for this module. */
export function resyncOffered(module: RerunFields): boolean {
    return module.rerun === undefined ? module.supportsResync : module.rerun === 'update';
}

/** Whether the dialog offers "Re-evaluate all records" for this module. */
export function rearmOffered(module: RerunFields): boolean {
    return module.rerun === 're-evaluate';
}

export interface LaunchConfirmView {
    readonly title: string;
    readonly sentence: string;
    readonly confirmLabel: string;
}

export function launchConfirmView(input: {
    readonly count: number;
    readonly moduleTitle: string;
    readonly environment: string;
    /** "Update from Salesforce" is ticked (and offered). */
    readonly resync?: boolean;
    /** "Re-evaluate all records" is ticked (and offered). */
    readonly rearmAll?: boolean;
}): LaunchConfirmView {
    const resync = input.resync === true;
    const rearmAll = !resync && input.rearmAll === true;
    const verb = resync ? 'Migrate and update' : rearmAll ? 'Migrate and re-evaluate' : 'Migrate';
    return {
        title: `${verb} ${input.moduleTitle} for ${clients(input.count)}`,
        sentence:
            `This starts a ${input.moduleTitle} migration from Salesforce into Veevart for ` +
            `${clients(input.count)} in ${input.environment}. ` +
            (resync
                ? 'Records already migrated are read from Salesforce again and updated. '
                : '') +
            (rearmAll
                ? 'Every record already migrated is re-evaluated, not only the ones whose ' +
                  'Salesforce record changed; what was changed in Veevart is kept. '
                : '') +
            'It writes Veevart data for each of them and cannot be undone from here.',
        confirmLabel: `${verb} ${clients(input.count)}`,
    };
}

/**
 * What the launch sends. `resync` and `rearm` each travel only when offered AND
 * ticked; `rearm` only ever as `'all'` (`'changed'` is the runner's default), so
 * a plain launch is byte-identical to the one an older runner takes.
 */
export interface LaunchInputView {
    readonly reason: string;
    readonly tenantIds: readonly string[];
    readonly resync?: true;
    readonly rearm?: 'all';
}

export function launchInput(input: {
    readonly reason: string;
    readonly tenantIds: readonly string[];
    readonly module: RerunFields;
    readonly resync: boolean;
    readonly rearmAll?: boolean;
}): LaunchInputView {
    return {
        reason: input.reason.trim(),
        tenantIds: input.tenantIds,
        ...(input.resync && resyncOffered(input.module) ? { resync: true as const } : {}),
        ...(input.rearmAll === true && rearmOffered(input.module) ? { rearm: 'all' as const } : {}),
    };
}

/** Why the dialog cannot submit yet, or `null` when it can. */
export function launchSubmitBlocker(input: {
    readonly count: number;
    readonly reason: string;
    readonly phraseMatches: boolean;
}): string | null {
    const count = launchBlocker(input.count);
    if (count) return count;
    const reason = input.reason.trim();
    if (reason.length === 0) return 'Say why — it is recorded on every run this starts.';
    if (reason.length > MAX_REASON) return `At most ${String(MAX_REASON)} characters.`;
    if (!input.phraseMatches) return 'Type the phrase to confirm.';
    return null;
}
